import { useEffect, useMemo, useState } from "react";

import { useCollections, useFavourites, useVideos } from "@/hooks";
import { Favourite } from "@/api";
import { ITEM_TYPE_COLLECTION, ITEM_TYPE_VIDEO } from "@/const.ts";
import { SaveToPlaylistProps } from "../types.ts";

const EMPTY_FAVOURITES: Favourite[] = [];

export const useFavouriteDrawer = ({
  ItemId,
  ItemType,
}: Pick<SaveToPlaylistProps, "ItemId" | "ItemType">) => {
  const [favourited, setFavourited] = useState(false);
  const { data, isFavourited } = useFavourites();
  const favourites = data?.Favourites ?? EMPTY_FAVOURITES;

  // Initialize state of whether this content is included in playlists
  useEffect(() => {
    if (data) {
      setFavourited(isFavourited({ id: ItemId, type: ItemType }));
    }
  }, [ItemId, ItemType, data, isFavourited]);
  const currentlyFavourited = isFavourited({ id: ItemId, type: ItemType });

  // Lookup the details of the first item
  // This is to render a thumbnail for the item
  const { videoIds, collectionIds } = useMemo(() => {
    const videoIds: number[] = [];
    const collectionIds: number[] = [];

    // Get the first item in favourites
    favourites.find((f) => {
      if (f.ItemType === ITEM_TYPE_COLLECTION) {
        collectionIds.push(parseInt(f.ItemId));
      }
      if (f.ItemType == ITEM_TYPE_VIDEO) {
        videoIds.push(parseInt(f.ItemId));
      }
      return f;
    });
    return { videoIds, collectionIds };
  }, [favourites]);
  const { videoById } = useVideos({ ids: videoIds });
  const { collectionById } = useCollections({ ids: collectionIds });
  const firstFavourite = useMemo(() => {
    const f = favourites.length > 0 ? favourites[0] : undefined;
    if (f && videoById && collectionById) {
      return f.ItemType === ITEM_TYPE_VIDEO
        ? videoById[parseInt(f.ItemId)]
        : collectionById[parseInt(f.ItemId)];
    }
    return undefined;
  }, [favourites, videoById, collectionById]);

  return {
    firstFavourite,
    favourites,
    currentlyFavourited,
    favourited,
    setFavourited,
    isLoading: !data,
  };
};
